"use client";

import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from "@/lib/i18n/hook";
import { useCurrency as useCurrencyContext } from "@/app/life-plan/contexts/currency-context";

interface LifePlanSummaryProps {
  data: any;
}

export function LifePlanSummary({ data }: LifePlanSummaryProps) {
  const { t } = useI18n();
  const { currency } = useCurrencyContext();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <Card className="col-span-1">
        <CardContent className="pt-4">
          <h3 className="text-base font-semibold mb-4 pb-2 border-b">
            {t("lifePlan.basicInfo.title")}
          </h3>
          <dl className="grid grid-cols-1 gap-2">
            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.basicInfo.name")}
              </dt>
              <dd className="font-medium">
                {data.basicInfo.name || t("common.notEntered")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.basicInfo.age")}
              </dt>
              <dd className="font-medium">
                {data.basicInfo.age}
                {t("common.years")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.basicInfo.gender.title")}
              </dt>
              <dd className="font-medium">
                {t(`lifePlan.basicInfo.gender.${data.basicInfo.gender}`)}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.basicInfo.spouse")}
              </dt>
              <dd className="font-medium">
                {data.basicInfo.familyStructure.hasSpouse
                  ? t("common.yes")
                  : t("common.no")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.basicInfo.children")}
              </dt>
              <dd className="font-medium">
                {data.basicInfo.familyStructure.children.length > 0
                  ? data.basicInfo.familyStructure.children.map(
                      (child: any, index: number) => (
                        <span key={index}>
                          {index > 0 && "、"}
                          {child.age}
                          {t("common.age")}
                        </span>
                      )
                    )
                  : t("common.none")}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card className="col-span-1">
        <CardContent className="pt-4">
          <h3 className="text-base font-semibold mb-4 pb-2 border-b">
            {t("lifePlan.incomeExpense.title")}
          </h3>
          <dl className="grid grid-cols-1 gap-2">
            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.annualIncome")}
              </dt>
              <dd className="font-medium">
                {data.income.annualIncome}
                {currency}
                {t("common.perYear")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.salaryIncreaseRate")}
              </dt>
              <dd className="font-medium">{data.income.salaryIncreaseRate}%</dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.annualBonus")}
              </dt>
              <dd className="font-medium">
                {data.income.annualBonus}
                {currency}
                {t("common.perYear")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.housingCost")}
              </dt>
              <dd className="font-medium">
                {data.expense.housingCost}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.utilities")}
              </dt>
              <dd className="font-medium">
                {data.expense.utilities}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.food")}
              </dt>
              <dd className="font-medium">
                {data.expense.food}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.communication")}
              </dt>
              <dd className="font-medium">
                {data.expense.communication}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.transportation")}
              </dt>
              <dd className="font-medium">
                {data.expense.transportation}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.entertainment")}
              </dt>
              <dd className="font-medium">
                {data.expense.entertainment}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.incomeExpense.other")}
              </dt>
              <dd className="font-medium">
                {data.expense.other}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card className="col-span-1">
        <CardContent className="pt-4">
          <h3 className="text-base font-semibold mb-4 pb-2 border-b">
            {t("lifePlan.assetsLiabilities.title")}
          </h3>
          <dl className="grid grid-cols-1 gap-2">
            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.savings")}
              </dt>
              <dd className="font-medium">
                {data.assets.savings.amount}
                {currency}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.stocks")}
              </dt>
              <dd className="font-medium">
                {data.assets.stocks.amount}
                {currency}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.bonds")}
              </dt>
              <dd className="font-medium">
                {data.assets.bonds.amount}
                {currency}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.mutualFunds")}
              </dt>
              <dd className="font-medium">
                {data.assets.mutualFunds.amount}
                {currency}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.insurance")}
              </dt>
              <dd className="font-medium">
                {data.assets.insurance.amount}
                {currency}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.otherInvestments")}
              </dt>
              <dd className="font-medium">
                {data.assets.other.amount}
                {currency}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.housingLoanBalance")}
              </dt>
              <dd className="font-medium">
                {data.liabilities.housingLoan[0].amount}
                {currency}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.housingLoanPeriod")}
              </dt>
              <dd className="font-medium">
                {data.liabilities.housingLoan[0].period}
                {t("common.years")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.housingLoanInterest")}
              </dt>
              <dd className="font-medium">
                {data.liabilities.housingLoan[0].interestRate}%
              </dd>
            </div>

            <div className="flex flex-col gap-1 text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.assetsLiabilities.otherLoans")}
              </dt>
              <dd className="font-medium">
                {data.liabilities.otherLoans.length > 0
                  ? data.liabilities.otherLoans.map(
                      (loan: any, index: number) => (
                        <div key={index} className="text-sm">
                          {loan.name}: {loan.amount}
                          {currency}（{loan.period}
                          {t("common.years")}、{loan.interestRate}%）
                        </div>
                      )
                    )
                  : t("common.none")}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card className="col-span-1">
        <CardContent className="pt-4">
          <h3 className="text-base font-semibold mb-4 pb-2 border-b">
            {t("lifePlan.lifeGoals.title")}
          </h3>
          <dl className="grid grid-cols-1 gap-2">
            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.lifeGoals.housingPurchase")}
              </dt>
              <dd className="font-medium">
                {data.lifeGoals.housingPurchase.planned
                  ? `${data.lifeGoals.housingPurchase.year}${t(
                      "common.age"
                    )}、${data.lifeGoals.housingPurchase.amount} ${currency}`
                  : t("lifePlan.lifeGoals.notPlanned")}
              </dd>
            </div>

            <div className="flex flex-col gap-1 text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.lifeGoals.educationFunds")}
              </dt>
              <dd className="font-medium">
                {data.lifeGoals.educationFunds.length > 0
                  ? data.lifeGoals.educationFunds.map(
                      (fund: any, index: number) => (
                        <div key={index} className="text-sm">
                          {fund.name}
                          {t("lifePlan.lifeGoals.child")}
                          {fund.age}
                          {t("common.age")}、{fund.amount}
                          {currency}
                        </div>
                      )
                    )
                  : t("common.none")}
              </dd>
            </div>

            <div className="flex flex-col gap-1 text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.lifeGoals.otherGoals")}
              </dt>
              <dd className="font-medium">
                {data.lifeGoals.otherGoals.length > 0
                  ? data.lifeGoals.otherGoals.map(
                      (goal: any, index: number) => (
                        <div key={index} className="text-xs">
                          {goal.name}: {goal.year}
                          {t("common.years")}、{goal.amount}
                        </div>
                      )
                    )
                  : t("common.none")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.lifeGoals.retirementAge")}
              </dt>
              <dd className="font-medium">
                {data.lifeGoals.retirementAge}
                {t("common.age")}
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.lifeGoals.retirementMonthlyExpense")}
              </dt>
              <dd className="font-medium">
                {data.lifeGoals.retirementMonthlyExpense}
                {currency}
                {t("common.perMonth")}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card className="col-span-1">
        <CardContent className="pt-4">
          <h3 className="text-base font-semibold mb-4 pb-2 border-b">
            {t("lifePlan.simulationSettings.title")}
          </h3>
          <dl className="grid grid-cols-1 gap-2">
            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.simulationSettings.inflationRate")}
              </dt>
              <dd className="font-medium">
                {data.simulationSettings.inflationRate}%
              </dd>
            </div>

            <div className="flex justify-between items-center text-sm">
              <dt className="text-muted-foreground">
                {t("lifePlan.simulationSettings.simulationPeriod")}
              </dt>
              <dd className="font-medium">
                {data.simulationSettings.simulationPeriod}
                {t("common.years")}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
